import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Center, Checkbox, Group, Loader, MultiSelect, Select, SimpleGrid, Stack, Text, TextInput, Textarea, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const EditJobPage: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [jobLoaded, setJobLoaded] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<boolean | null>(null);
  const [approvalUpdating, setApprovalUpdating] = useState(false);

  const experienceOptions: { value: string; label: string }[] = [
    { value: '0_1', label: t('experienceRanges.zeroToOne') },
    { value: '2_3', label: t('experienceRanges.twoToThree') },
    { value: '4_6', label: t('experienceRanges.fourToSix') },
    { value: '7_PLUS', label: t('experienceRanges.sevenPlus') },
  ];

  const careerOptions = [
    { value: 'MUSICOTERAPIA', label: t('careers.musicotherapy') },
    { value: 'PSICOLOGIA', label: t('careers.psychology') },
    { value: 'PROFESORADO_PSICOLOGIA', label: t('careers.psychologyTeaching') },
    { value: 'TERAPIA_OCUPACIONAL', label: t('careers.occupationalTherapy') },
  ];

  const positionOptions = [
    { value: 'THERAPEUTIC_ASSISTANT', label: t('jobCategories.therapeuticAssistant') },
    { value: 'SCHOOL_ASSISTANT', label: t('jobCategories.schoolAssistant') },
    { value: 'HR', label: t('jobCategories.hr') },
    { value: 'PSYCHOLOGY_PRO', label: t('jobCategories.psychologyProfessional') },
    { value: 'DOCENTE_TUTOR', label: t('jobCategories.docenteTutor') },
    { value: 'MUSICOTERAPEUTA', label: t('jobCategories.musicoterapeuta') },
    { value: 'TERAPISTA_OCUPACIONAL', label: t('jobCategories.terapistaOcupacional') },
    { value: 'SELECTOR_PERSONAL', label: t('jobCategories.selectorPersonal') },
    { value: 'PERSONAL_ADMINISTRATIVO', label: t('jobCategories.personalAdministrativo') },
    { value: 'ORIENTADOR_SALA', label: t('jobCategories.orientadorSala') },
    { value: 'ACOMPANANTE_GERONTOLOGICO', label: t('jobCategories.acompananteGerontologico') },
    { value: 'COORDINADOR_INTEGRACIONES', label: t('jobCategories.coordinadorIntegraciones') },
    { value: 'SUPERVISOR_ATS', label: t('jobCategories.supervisorAts') },
    { value: 'OTHER', label: t('jobCategories.other') },
  ];

  const normalizePositionValue = (value: string) =>
    positionOptions.find((option) => option.value === value || option.label === value)?.value || value;

  const resolvePositionLabel = (value: string) =>
    positionOptions.find((option) => option.value === value)?.label || value;

  const isOtherSelected = (value: string) => normalizePositionValue(value) === 'OTHER';

  const [form, setForm] = useState({
    title: '',
    company_name: '',
    location: '',
    is_remote: false,
    job_type: 'FULL_TIME',
    experience_range: '0_1',
    short_description: '',
    description: '',
    requirements: '',
    requires_at_title: false,
    requires_at_diploma_uba: false,
    requires_graduated: false,
    requires_school_inclusion_course: false,
    requires_hr_courses: false,
    careers_affine: [] as string[],
    position_option: 'THERAPEUTIC_ASSISTANT',
    position_other: ''
  });

  useEffect(() => {
    loadJob();
  }, [id]);

  const loadJob = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getJob(Number(id));

      // Populate form with job data
      const matchedOption = positionOptions.find((option) => option.label === data.title);
      const positionOption = matchedOption ? matchedOption.value : 'OTHER';
      const positionOther = matchedOption ? '' : (data.title || '');

      setForm({
        title: data.title || '',
        company_name: data.company_name || '',
        location: data.location || '',
        is_remote: data.is_remote || false,
        job_type: data.job_type || 'FULL_TIME',
        experience_range: data.experience_range || '0_1',
        short_description: data.short_description || '',
        description: data.description || '',
        requirements: data.requirements || '',
        requires_at_title: data.requires_at_title || false,
        requires_at_diploma_uba: data.requires_at_diploma_uba || false,
        requires_graduated: data.requires_graduated || false,
        requires_school_inclusion_course: data.requires_school_inclusion_course || false,
        requires_hr_courses: data.requires_hr_courses || false,
        careers_affine: data.careers_affine || [],
        position_option: positionOption,
        position_other: positionOther
      });
      setApprovalStatus(typeof data.is_approved === 'boolean' ? data.is_approved : null);
      setJobLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job');
      setJobLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    if (form.careers_affine.length === 0) {
      setError(t('createJob.careersRequired'));
      setSaving(false);
      return;
    }

    try {
      const normalizedPosition = normalizePositionValue(form.position_option);
      const titleValue = normalizedPosition === 'OTHER'
        ? form.position_other.trim()
        : resolvePositionLabel(normalizedPosition);

      await api.updateJob(Number(id), {
        title: titleValue,
        company_name: form.company_name,
        location: form.location,
        is_remote: form.is_remote,
        job_type: form.job_type,
        experience_range: form.experience_range,
        short_description: form.short_description,
        description: form.description,
        requirements: form.requirements,
        requires_at_title: form.requires_at_title,
        requires_at_diploma_uba: form.requires_at_diploma_uba,
        requires_graduated: form.requires_graduated,
        requires_school_inclusion_course: form.requires_school_inclusion_course,
        requires_hr_courses: form.requires_hr_courses,
        careers_affine: form.careers_affine,
      });

      setSuccess(true);
      setTimeout(() => navigate(`/jobs/${id}`), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update job posting');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveJob = async () => {
    if (!id) return;
    try {
      setApprovalUpdating(true);
      await api.approveJob(Number(id));
      setApprovalStatus(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.approveJobError'));
    } finally {
      setApprovalUpdating(false);
    }
  };

  const handleRejectJob = async () => {
    if (!id) return;
    try {
      setApprovalUpdating(true);
      await api.rejectJob(Number(id));
      setApprovalStatus(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.rejectJobError'));
    } finally {
      setApprovalUpdating(false);
    }
  };

  const canManageApproval = user?.user_type === 'ADMIN' || user?.user_type === 'MODERATOR';

  if (user?.user_type === 'JOB_SEEKER') {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Title order={3}>{t('common.accessRestricted')}</Title>
        <Text c="dimmed">{t('common.onlyPosters')}</Text>
      </Card>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (error && !jobLoaded) {
    return (
      <Card withBorder padding="xl" radius="lg" shadow="sm">
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {error}
          </Alert>
          <Button variant="outline" color="plum" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Card withBorder padding="xl" radius="lg" shadow="sm">
      <Stack gap="md">
        <Title order={2}>{t('editJob.title')}</Title>

        {canManageApproval && (
          <Group gap="sm" align="center">
            <Badge color={approvalStatus ? 'green' : 'red'} variant="light">
              {approvalStatus ? t('users.table.approved') : t('approvals.pendingApproval')}
            </Badge>
            <Button
              size="xs"
              color="green"
              leftSection={<IconCheck size={16} />}
              loading={approvalUpdating}
              onClick={handleApproveJob}
            >
              {t('approvals.approve')}
            </Button>
            <Button
              size="xs"
              color="red"
              variant="outline"
              leftSection={<IconAlertCircle size={16} />}
              loading={approvalUpdating}
              onClick={handleRejectJob}
            >
              {t('approvals.reject')}
            </Button>
          </Group>
        )}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
            {error}
          </Alert>
        )}

        {success && (
          <Alert icon={<IconCheck size={16} />} title="Success" color="green" radius="md">
            Job updated successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={onSubmit}>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Select
                label={t('createJob.fields.title')}
                value={normalizePositionValue(form.position_option)}
                onChange={(value) => {
                  if (!value) return;
                  const normalizedValue = normalizePositionValue(value);
                  if (normalizedValue === 'OTHER') {
                    setForm((prev) => ({
                      ...prev,
                      position_option: normalizedValue,
                      title: prev.position_other.trim(),
                    }));
                    return;
                  }
                  setForm((prev) => ({
                    ...prev,
                    position_option: normalizedValue,
                    position_other: '',
                    title: resolvePositionLabel(normalizedValue),
                  }));
                }}
                data={positionOptions}
                required
                disabled={saving || success}
              />
              {isOtherSelected(form.position_option) ? (
                <TextInput
                  label={t('createJob.fields.categoryOther')}
                  value={form.position_other}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      position_option: 'OTHER',
                      position_other: value,
                      title: value,
                    }));
                  }}
                  disabled={saving || success}
                  required
                />
              ) : (
                <TextInput
                  label={t('createJob.fields.company')}
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  required
                  disabled={saving || success}
                />
              )}
              {isOtherSelected(form.position_option) && (
                <TextInput
                  label={t('createJob.fields.company')}
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  required
                  disabled={saving || success}
                />
              )}
              <TextInput
                label={t('createJob.fields.location')}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                required
                disabled={saving || success}
              />
              <Select
                label={t('createJob.fields.jobType')}
                value={form.job_type}
                onChange={(value) => value && setForm({ ...form, job_type: value })}
                data={[
                  { value: 'FULL_TIME', label: 'Full Time' },
                  { value: 'PART_TIME', label: 'Part Time' },
                  { value: 'INTERNSHIP', label: 'Pasante' },
                  { value: 'TEMPORARY', label: 'Temporal' },
                ]}
                disabled={saving || success}
              />
              <Select
                label={t('createJob.fields.experience')}
                value={form.experience_range}
                onChange={(value) => value && setForm({ ...form, experience_range: value })}
                data={experienceOptions}
                disabled={saving || success}
              />
              <Checkbox
                label={t('createJob.fields.remote')}
                checked={form.is_remote}
                onChange={(e) => setForm({ ...form, is_remote: e.currentTarget.checked })}
                disabled={saving || success}
              />
            </SimpleGrid>
            <Textarea
              label={t('createJob.fields.shortDescription')}
              value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              autosize
              minRows={2}
              maxLength={120}
              required
              disabled={saving || success}
            />
            <Textarea
              label={t('createJob.fields.description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              autosize
              minRows={4}
              required
              disabled={saving || success}
            />
            <Stack gap="xs">
              <Text fw={600}>{t('createJob.requirementsTitle')}</Text>
              <Checkbox
                label={t('createJob.fields.requiresAtCourse')}
                checked={form.requires_at_title}
                onChange={(e) => setForm({ ...form, requires_at_title: e.currentTarget.checked })}
                disabled={saving || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresAtDiplomaUba')}
                checked={form.requires_at_diploma_uba}
                onChange={(e) => setForm({ ...form, requires_at_diploma_uba: e.currentTarget.checked })}
                disabled={saving || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresDegreeTitle')}
                checked={form.requires_graduated}
                onChange={(e) => setForm({ ...form, requires_graduated: e.currentTarget.checked })}
                disabled={saving || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresSchoolInclusionCourse')}
                checked={form.requires_school_inclusion_course}
                onChange={(e) => setForm({ ...form, requires_school_inclusion_course: e.currentTarget.checked })}
                disabled={saving || success}
              />
              <Checkbox
                label={t('createJob.fields.requiresHrCourses')}
                checked={form.requires_hr_courses}
                onChange={(e) => setForm({ ...form, requires_hr_courses: e.currentTarget.checked })}
                disabled={saving || success}
              />
              <Textarea
                label={t('createJob.fields.otherSkills')}
                value={form.requirements}
                onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                autosize
                minRows={2}
                disabled={saving || success}
              />
            </Stack>
            <MultiSelect
              label={t('createJob.fields.careers')}
              data={careerOptions}
              value={form.careers_affine}
              onChange={(value) => setForm({ ...form, careers_affine: value })}
              placeholder={t('createJob.fields.careersPlaceholder')}
              required
              disabled={saving || success}
            />
            <Group gap="sm">
              <Button
                type="submit"
                radius="md"
                size="md"
                loading={saving}
                disabled={success}
              >
                {t('editJob.save')}
              </Button>
              <Button
                variant="outline"
                color="plum"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Card>
  );
};

export default EditJobPage;
